import { Button } from '@odekoteam/doppio';
import { FORM_ERROR } from 'final-form';
import { useMemo } from 'react';
import { Field, Form } from 'react-final-form';
import { Box, Flex, Grid, Heading, Input } from 'theme-ui';

import { ErrorLabel } from '@/components/Collection/Error';
import { formatDateRange } from '@/components/Collection/utils';
import { required } from '@/components/forms/utils/Validators';
import Modal from '@/components/Modal';
import {
  DuplicateMasterHomepageMutation,
  GetMasterHomepageForEditQuery,
  GetMasterHomepagesQuery,
  UpsertMasterHomepageMutation,
  useDuplicateMasterHomepageMutation,
  useUpsertMasterHomepageMutation,
} from '@/graphql/types/generated';
import { useAppDispatch } from '@/hooks/store';
import { showToast, ToastAlertType } from '@/state/toast';

type UpsertFormData = {
  endDate: string;
  name: string;
  startDate: string;
};

function getNextDateString(dateString: string) {
  const [year, month, day] = dateString.split('-').map(Number);
  const nextDate = new Date(Date.UTC(year, month - 1, day));
  nextDate.setUTCDate(nextDate.getUTCDate() + 1);
  return nextDate.toISOString().split('T')[0];
}

function getOverlappingHomepage(
  values: Pick<UpsertFormData, 'startDate' | 'endDate'>,
  masterHomepages: GetMasterHomepagesQuery['masterHomepages'],
  currentHomepageId?: string,
) {
  if (
    !values.startDate ||
    !values.endDate ||
    values.endDate < values.startDate
  ) {
    return null;
  }
  return (
    masterHomepages.find((masterHomepage) => {
      if (masterHomepage.id === currentHomepageId) {
        return false;
      }
      const startDate = String(masterHomepage.startDate).split('T')[0];
      const endDate = String(masterHomepage.endDate).split('T')[0];
      return values.startDate <= endDate && values.endDate >= startDate;
    }) ?? null
  );
}

export function ModalUpsertHomepage({
  mode,
  close,
  onSaved,
  homepage,
  masterHomepages,
}: {
  close: () => void;
  mode: 'create' | 'edit' | 'duplicate' | null;
  onSaved: (masterHomepageId: string) => void;
  homepage: NonNullable<
    GetMasterHomepageForEditQuery['masterHomepageForEdit']
  > | null;
  masterHomepages: GetMasterHomepagesQuery['masterHomepages'];
}) {
  const dispatch = useAppDispatch();
  const [, executeUpsert] = useUpsertMasterHomepageMutation();
  const [, executeDuplicate] = useDuplicateMasterHomepageMutation();

  const initialValues = useMemo<UpsertFormData>(() => {
    if (mode === 'create' || !homepage) {
      return { endDate: '', name: '', startDate: '' };
    }
    const toDateString = (d: Date) =>
      d instanceof Date ? d.toISOString().split('T')[0] : String(d);
    return {
      endDate: toDateString(homepage.endDate),
      name: mode === 'duplicate' ? `${homepage.name} (COPY)` : homepage.name,
      startDate: toDateString(homepage.startDate),
    };
  }, [homepage, mode]);

  const onSubmit = async (values: UpsertFormData) => {
    if ((mode === 'edit' || mode === 'duplicate') && !homepage) {
      return { [FORM_ERROR]: 'No master homepage selected' };
    }
    let payload:
      | undefined
      | UpsertMasterHomepageMutation['upsertMasterHomepage']
      | DuplicateMasterHomepageMutation['duplicateMasterHomepage'];

    if (mode === 'duplicate' && homepage) {
      const result = await executeDuplicate({
        input: {
          endDate: values.endDate,
          id: homepage.id,
          name: values.name,
          startDate: values.startDate,
        },
      });
      if (result.error) {
        return { [FORM_ERROR]: result.error.message };
      }
      payload = result.data?.duplicateMasterHomepage;
    } else {
      const result = await executeUpsert({
        input:
          mode === 'edit' && homepage
            ? { ...values, id: homepage.id }
            : { ...values },
      });
      if (result.error) {
        return { [FORM_ERROR]: result.error.message };
      }
      payload = result.data?.upsertMasterHomepage;
    }
    const errors = payload?.errors?.map((e) => e.message) ?? [];
    if (errors.length > 0 || !payload?.masterHomepage) {
      return {
        [FORM_ERROR]:
          errors.length > 0
            ? errors.join(', ')
            : 'Unable to save master homepage',
      };
    }
    onSaved(payload.masterHomepage.id);
    switch (mode) {
      case 'create':
        dispatch(
          showToast({
            duration: 4000,
            alertType: ToastAlertType.SUCCESS,
            header: 'Master homepage created',
            content: 'The master homepage has been created.',
          }),
        );
        break;
      case 'edit':
        dispatch(
          showToast({
            duration: 4000,
            alertType: ToastAlertType.SUCCESS,
            header: 'Master homepage updated',
            content: 'The master homepage has been updated.',
          }),
        );
        break;
      case 'duplicate':
        dispatch(
          showToast({
            duration: 4000,
            alertType: ToastAlertType.SUCCESS,
            header: 'Master homepage duplicated',
            content: 'The master homepage has been duplicated.',
          }),
        );
        break;
    }
    close();
    return;
  };

  return (
    <Modal visible={mode !== null}>
      <Form<UpsertFormData>
        onSubmit={onSubmit}
        initialValues={initialValues}
        render={({ form, values, submitting, submitError, handleSubmit }) => {
          const overlappingHomepage = getOverlappingHomepage(
            values,
            masterHomepages,
            mode === 'edit' ? homepage?.id : undefined,
          );
          return (
            <>
              <Heading variant="headingMd" sx={{ mb: 3 }}>
                {mode === 'create' && 'Create New Collection Homepage'}
                {mode === 'edit' && 'Edit Homepage'}
                {mode === 'duplicate' && 'Duplicate Homepage'}
              </Heading>
              <Flex sx={{ gap: 4, flexDirection: 'column' }}>
                <div>
                  {mode === 'create' &&
                    'Set up a new homepage version with a custom name and date range.'}
                  {mode === 'edit' &&
                    'Update the homepage name and date range.'}
                  {mode === 'duplicate' &&
                    `Create a copy of ${homepage?.name} with a new name and date range.`}
                </div>
                <Field name="name" validate={required}>
                  {({ input, meta }) => {
                    const error =
                      (meta.touched && meta.error) || meta.submitError;
                    return (
                      <Box>
                        <Heading variant="headingXs">Homepage Name</Heading>
                        <Input
                          {...input}
                          autoCorrect="off"
                          autoComplete="off"
                          data-lpignore="true"
                          autoCapitalize="off"
                          placeholder="April 2026"
                          id="master-homepage-name"
                          spellCheck={false}
                          disabled={submitting}
                          sx={{
                            borderColor: error ? 'errorDefault' : undefined,
                          }}
                          data-1p-ignore
                        />
                        <ErrorLabel message={error} />
                      </Box>
                    );
                  }}
                </Field>
                <Grid sx={{ gap: 3, gridTemplateColumns: '1fr 1fr' }}>
                  <Field name="startDate" validate={required}>
                    {({ input, meta }) => {
                      const error =
                        (meta.touched && meta.error) || meta.submitError;
                      return (
                        <Box>
                          <Heading variant="headingXs">Start Date</Heading>
                          <Input
                            {...input}
                            type="date"
                            placeholder="Select date"
                            id="master-homepage-start-date"
                            disabled={submitting}
                            onChange={(event) => {
                              input.onChange(event);
                              const nextStartDate = event.target.value;
                              if (!nextStartDate) {
                                form.change('endDate', '');
                                return;
                              }
                              const nextEndDate =
                                getNextDateString(nextStartDate);
                              if (
                                !values.endDate ||
                                values.endDate < nextStartDate
                              ) {
                                form.change('endDate', nextEndDate);
                              }
                            }}
                            sx={{
                              borderColor: error ? 'errorDefault' : undefined,
                            }}
                          />
                          <ErrorLabel message={error} />
                        </Box>
                      );
                    }}
                  </Field>
                  <Field name="endDate" validate={required}>
                    {({ input, meta }) => {
                      const error =
                        (meta.touched && meta.error) || meta.submitError;
                      return (
                        <Box>
                          <Heading variant="headingXs">End Date</Heading>
                          <Input
                            {...input}
                            type="date"
                            placeholder="Select date"
                            id="master-homepage-end-date"
                            disabled={submitting}
                            min={values.startDate || undefined}
                            sx={{
                              borderColor: error ? 'errorDefault' : undefined,
                            }}
                          />
                          <ErrorLabel message={error} />
                        </Box>
                      );
                    }}
                  </Field>
                </Grid>
                {values.name && values.startDate && values.endDate && (
                  <Box
                    sx={{
                      p: 2,
                      mt: 1,
                      bg: '#FFFEF5',
                      border: '1px solid',
                      borderColor: '#FFD100',
                    }}
                  >
                    <div>Preview:</div>
                    <Heading as="h4" variant="headingSm" sx={{ mt: 1, mb: 1 }}>
                      {values.name || '(Untitled)'}
                    </Heading>
                    <div>
                      {formatDateRange(values.startDate, values.endDate)}
                    </div>
                  </Box>
                )}
                <ErrorLabel
                  message={
                    overlappingHomepage
                      ? `These dates overlap with ${
                          overlappingHomepage.name
                        } (${formatDateRange(
                          overlappingHomepage.startDate,
                          overlappingHomepage.endDate,
                        )}).`
                      : undefined
                  }
                />
                <ErrorLabel message={submitError} />
                <Flex sx={{ gap: 3, justifyContent: 'flex-end' }}>
                  <Button variant="ghost" onPress={close} disabled={submitting}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onPress={handleSubmit}
                    isDisabled={submitting || !!overlappingHomepage}
                  >
                    {mode === 'create' && 'Create Homepage'}
                    {mode === 'edit' && 'Save'}
                    {mode === 'duplicate' && 'Duplicate'}
                  </Button>
                </Flex>
              </Flex>
            </>
          );
        }}
      />
    </Modal>
  );
}
